import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';
import { errorMessage } from '../lib/format';
import { haptic } from '../lib/telegram';
import type { KhqrOrder, Plan } from '../lib/types';
import { Icon } from './Icon';
import { KhqrCard } from './KhqrCard';
import { Sheet } from './Sheet';
import { useToast } from './Toast';

/**
 * The Bakong API allows 100 requests a day, so the app doesn't poll freely:
 * it checks when the buyer taps "I've paid", then a few more times.
 */
const RECHECK_MS = 30_000;
const RECHECKS = 6;

const clock = (ms: number) => {
  const s = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

/**
 * Pay the app owner by KHQR: shows the QR (with "Save QR image" for paying
 * from ABA's scan-from-photo), then checks every few seconds until Bakong
 * confirms the payment. `create` makes a fresh order (Pro or a tip).
 */
export function KhqrPaySheet({
  open,
  onClose,
  create,
  onPaid,
}: {
  open: boolean;
  onClose: () => void;
  create: () => Promise<KhqrOrder>;
  onPaid?: (order: KhqrOrder) => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [order, setOrder] = useState<KhqrOrder | null>(null);
  const [status, setStatus] = useState<KhqrOrder['status']>('pending');
  const [now, setNow] = useState(Date.now());
  const [checking, setChecking] = useState(false);
  const [checks, setChecks] = useState(0);

  const start = async () => {
    setOrder(null);
    setStatus('pending');
    setChecks(0);
    try {
      setOrder(await create());
    } catch (error) {
      toast(errorMessage(error, t), 'error');
      onClose();
    }
  };

  useEffect(() => {
    if (open) void start();
    else setOrder(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const check = async () => {
    if (!order || checking) return;
    setChecking(true);
    try {
      const result = await api.checkKhqrOrder(order.id);
      queryClient.setQueryData<Plan>(['plan'], (old) => (old ? { ...old, ...result.plan } : result.plan));
      setStatus(result.status);
      if (result.status === 'paid') {
        haptic.success();
        onPaid?.(order);
      }
    } catch (error) {
      toast(errorMessage(error, t), 'error');
    } finally {
      setChecking(false);
      setChecks((n) => n + 1);
    }
  };

  // Countdown while the QR is valid.
  useEffect(() => {
    if (!order || status !== 'pending') return;
    const tick = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(tick);
  }, [order, status]);

  // After "I've paid", re-check a few times (bank transfers can take a moment).
  useEffect(() => {
    if (!order || status !== 'pending' || checks === 0 || checks > RECHECKS) return;
    const timer = window.setTimeout(() => void check(), RECHECK_MS);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, status, checks]);

  return (
    <Sheet open={open} onClose={onClose} title={t('pay.title')}>
      {!order ? (
        <div className="empty">
          <Icon name="loader" size={28} className="spin" />
        </div>
      ) : status === 'paid' ? (
        <div className="pay-done">
          <span className="pay-done-icon">
            <Icon name="check" size={34} />
          </span>
          <h3>{order.confirmable === false ? t('pay.thanksTitle') : t('pay.received')}</h3>
          <p className="muted">{order.product === 'pro' ? t('pro.welcome') : t('pay.thanksTip')}</p>
          <button className="btn btn--primary btn--block" onClick={onClose}>
            {t('common.close')}
          </button>
        </div>
      ) : (
        <div className="stack" style={{ alignItems: 'center', textAlign: 'center' }}>
          <KhqrCard khqr={order} />
          {status === 'expired' || Date.parse(order.expiresAt) <= now ? (
            <>
              <div className="alert">{t('pay.expired')}</div>
              <button className="btn btn--primary btn--block" onClick={() => void start()}>
                {t('pay.newQr')}
              </button>
            </>
          ) : (
            <>
              <div className="pay-waiting">
                {checks > 0 && <span className="pay-pulse" aria-hidden />}
                <span>
                  {order.confirmable === false
                    ? t('pay.scanThenDone')
                    : checking
                      ? t('pay.checking')
                      : checks > 0
                        ? t('pay.notYet')
                        : t('pay.tapWhenPaid')}
                </span>
                <span className="muted small num">{t('pay.expiresIn', { time: clock(Date.parse(order.expiresAt) - now) })}</span>
              </div>
              {order.confirmable === false ? (
                // Nothing to unlock and no way to confirm yet: thank the giver.
                <button className="btn btn--primary btn--block" onClick={() => (haptic.success(), setStatus('paid'))}>
                  <Icon name="check" size={18} />
                  {t('pay.done')}
                </button>
              ) : (
                <button className="btn btn--primary btn--block" disabled={checking} onClick={() => void check()}>
                  <Icon name={checking ? 'loader' : 'check'} size={18} className={checking ? 'spin' : undefined} />
                  {t('settle.iPaid')}
                </button>
              )}
            </>
          )}
        </div>
      )}
    </Sheet>
  );
}

/** Profile card: tip the app by KHQR ($1 / $2 / $5). Hidden where KHQR payment isn't set up. */
export function SupportCard() {
  const { t } = useTranslation();
  const [amount, setAmount] = useState<string | null>(null);
  return (
    <div className="card stack">
      <div className="row">
        <span className="icon-tile icon-tile--ink" aria-hidden>
          <Icon name="heart" size={20} />
        </span>
        <div className="grow">
          <div className="list-title">{t('support.title')}</div>
          <div className="muted small">{t('support.body')}</div>
        </div>
      </div>
      <div className="support-amounts">
        {['1', '2', '5'].map((usd) => (
          <button key={usd} type="button" className="btn btn--soft" onClick={() => (haptic.select(), setAmount(usd))}>
            ${usd}
          </button>
        ))}
      </div>
      <KhqrPaySheet open={amount !== null} onClose={() => setAmount(null)} create={() => api.tipKhqr(amount ?? '1')} />
    </div>
  );
}
